import { fromJS, Iterable, Map } from 'immutable';
import moment from 'moment';

import { withStatePrefix } from './decorators/handleApiError';
import { autoNullId, autoRemoveId, check } from './decorators/suggest';
import { toJS, spy, setIn, mergeDeep } from '../utils/ChangeSpy';
import * as ActionType from '../actions/meetings';
import getValidator from '../utils/getValidator';

const defaultShowAdditionField = fromJS({
  showDescription: false,
  showDealNotes: false,
  showEmail: false,
  showNDAExecuted: false,
  showOfferAmount: false,
  showFuture: false,
  showPast: false,
});

const SCHEMA_TARGET = {
  type: 'object',
  properties: {
    date: {
      title: 'Date',
      description: 'should be a date',
      oneOf: [
        { type: 'null' },
        { type: 'string', pattern: '' },
        { type: 'string', pattern: /^[^\s]{10}$/ },
        { type: 'object' },
      ],
      required: true,
    },
    activityMap: {
      type: 'object',
      properties: {
        text: {
          title: 'Activity',
          description: 'should not be empty or whitespace',
          type: 'string',
          pattern: '^\\s*\\S[\\s\\S]*$',
        },
        selected: {
          type: 'object',
          minProperties: 0,
        },
      },
      required: ['text', 'selected'],
    },
    targetId: {
      title: 'Target',
      description: 'should not be empty',
      type: 'number',
      required: true,
      minLength: 1,
    },
    recordSubOwnerId: {
      title: 'Analyst',
      description: 'should not be empty',
      type: 'number',
      required: true,
      minLength: 1,
    },
    type: {
      title: 'Type',
      description: 'should not be empty',
      type: 'string',
      required: true,
      minValue: 1,
    },
    buyerId: {
      title: 'Buyer',
      description: 'should not be empty',
      type: 'number',
      required: true,
      minValue: 1,
    },
    eventId: {
      title: 'Event',
      description: 'should not be empty',
      oneOf: [{ type: 'null' }, { type: 'string', minLength: 1 }],
    },
    projectId: {
      title: 'Project',
      description: 'should not be empty',
      type: 'number',
      required: true,
      minValue: 1,
    },
  },
};

const SCHEMA_BUYER = {
  type: 'object',
  properties: {
    date: {
      title: 'Date',
      description: 'should be a date',
      oneOf: [
        { type: 'null' },
        { type: 'string', pattern: '' },
        { type: 'string', pattern: /^[^\s]{10}$/ },
        { type: 'object' },
      ],
      required: true,
    },
    activityMap: {
      type: 'object',
      properties: {
        text: {
          title: 'Activity',
          description: 'should not be empty or whitespace',
          type: 'string',
          pattern: '^\\s*\\S[\\s\\S]*$',
        },
        selected: {
          type: 'object',
          minProperties: 0,
        },
      },
      required: ['text', 'selected'],
    },
    targetId: {
      title: 'Target',
      description: 'should not be empty',
      type: 'number',
      required: true,
      minLength: 1,
    },
    type: {
      title: 'Type',
      description: 'should not be empty',
      type: 'string',
      required: true,
      minValue: 1,
    },
    eventId: {
      title: 'Event',
      description: 'should not be empty',
      oneOf: [{ type: 'null' }, { type: 'string', minLength: 1 }],
    },
  },
};

const FIELDS_TO_SPY = [
  'targetId',
  'targetName',
  'buyerId',
  'buyerName',
  'recordOwnerId',
  'recordOwnerName',
  'contactId',
  'recordSubOwnerId',
  'recordSubOwnerName',
  'dealMakerId',
  'dealMakerName',
  'projectId',
  'projectName',
  'userId',
  'userName',
  'eventId',
  'eventName',
  'dealNotes',
  'summaryHtml',
  'title',
  'activityMap.text',
  'date',
  'expiresAt',
  'type',
];

const defaultState = fromJS({
  predefinedSuggestTarget: null,
  isLoading: false,
  isLoadingCompletedEvents: false,
  current: spy(
    {
      targetId: '',
      targetName: '',
      buyerId: '',
      buyerName: '',
      recordOwnerId: '',
      recordOwnerName: '',
      contactId: 0,
      recordSubOwnerId: '',
      recordSubOwnerName: '',
      dealMakerId: '',
      dealMakerName: '',
      contacts: [],
      projectId: '',
      projectName: '',
      userId: '',
      userName: '',
      eventId: '',
      eventName: '',
      dealNotes: '',
      summaryHtml: '',
      title: '',
      date: moment(),
      expiresAt: moment(),
      isValid: false,
      activityMap: {
        text: '',
        selected: {},
        suggestions: [],
      },
      type: null,
    },
    FIELDS_TO_SPY,
  ),
  initialPrefilledTarget: null,
});

let wrappedReducer = withStatePrefix(ActionType.CREATE_EVENT_FROM_FIREFLY_ERROR, reducer, () => [
  'current',
  'inputErrors',
]);

wrappedReducer = check('current.targetName', 'suggestTarget', ActionType.FETCHED_EVENT_INFO_SUCCESS, wrappedReducer);
wrappedReducer = check('current.recordOwnerName', 'suggestUser', ActionType.FETCHED_EVENT_INFO_SUCCESS, wrappedReducer);
wrappedReducer = autoNullId('current.targetName', 'current.targetId', ActionType.UPDATE_FIELD, wrappedReducer);
wrappedReducer = autoNullId(
  'current.recordOwnerName',
  'current.recordOwnerId',
  ActionType.UPDATE_FIELD,
  wrappedReducer,
);
wrappedReducer = autoNullId('current.userName', 'current.userId', ActionType.UPDATE_FIELD, wrappedReducer);
wrappedReducer = autoRemoveId(
  'current.recordOwnerName',
  'current.recordOwnerId',
  ActionType.UPDATE_FIELD,
  wrappedReducer,
);
wrappedReducer = autoRemoveId('current.userName', 'current.userId', ActionType.UPDATE_FIELD, wrappedReducer);

export default wrappedReducer;

function reducer(state = defaultState, action) {
  switch (action.type) {
    case ActionType.UPDATE_FIELD: {
      if (action.name === 'current.targetId') {
        const type = state.getIn(['current', 'type', 'value']);
        const eventValue = type === 'update_completed_action' ? '' : null;

        return checkValidity(
          state.setIn(['current', 'eventId'], eventValue).setIn(['current', 'targetId', 'value'], action.value),
        );
      }

      if (action.name === 'current.targetName') {
        let nextState = setIn(state, action.name, action.value);

        if (action.value === '') {
          nextState = setIn(nextState, 'current.targetId', null);
        }

        return checkValidity(nextState);
      }

      if (action.name === 'current.type') {
        const eventValue = action.value === 'update_completed_action' ? '' : null;

        return checkValidity(
          state.setIn(['current', 'eventId'], eventValue).setIn(['current', 'type', 'value'], action.value),
        );
      }

      if (action.name === 'current.activityMap') {
        Object.keys(action.value).forEach(key => {
          if (key === 'selected') {
            state = setIn(state, 'current.activityMap.selected', defaultShowAdditionField.merge(action.value.selected));
          } else if (Iterable.isIterable(action.value[key])) {
            state = setIn(state, `current.activityMap.${key}`, action.value[key]);
          } else {
            state = mergeDeep(state, {
              [`current.activityMap.${key}`]: action.value[key],
            });
          }
        });

        return checkValidity(state);
      }

      return checkValidity(setIn(state, action.name, action.value));
    }

    case ActionType.LOADED_USERS_SUCCESS:
      return state.set(
        'suggestUser',
        fromJS(
          action.response.data.map(target => ({
            text: target.text,
            id: target.id,
          })),
        ),
      );

    case ActionType.FETCH_MEETING_SUCCESS: {
      const detailsState = state
        .set(
          'predefinedSuggestTarget',
          fromJS(
            action.response.data.companies.map(target => ({
              text: target.legalName,
              id: target.id,
            })),
          ),
        )
        .setIn(['current', 'meetingId'], action.response.data.meetingId)
        .setIn(['current', 'meetingLink'], action.response.data.meetingLink)
        .setIn(['current', 'dealNotes', 'value'], action.response.data.summaryData)
        .setIn(['current', 'summaryHtml', 'value'], action.response.data.summaryHtml)
        .setIn(['current', 'title', 'value'], action.response.data.title)
        .setIn(['current', 'participantsText'], getParticipantsTextList(action.response.data.participants))
        .setIn(['current', 'date', 'value'], moment(action.response.data.meetingDateTime))
        .setIn(['current', 'expiresAt', 'value'], moment(action.response.data.expiresAt))
        .setIn(['current', 'activityMap', 'text', 'value'], action.response.data.eventType)
        .setIn(['current', 'userId', 'value'], action.user.get('id'))
        .setIn(['current', 'userName', 'value'], action.user.get('userName'));

      return detailsState.set('initialPrefilledTarget', detailsState.get('current'));
    }

    case ActionType.FETCHED_EVENT_INFO_SUCCESS: {
      const predefined = state.get('predefinedSuggestTarget') || fromJS([]);
      const fetched = fromJS(
        action.response.data.map(target => ({
          text: target.legalName.replaceAll('\t', ''),
          id: target.id,
        })),
      );

      const combined = predefined.concat(fetched);

      return state.set('suggestTarget', combined);
    }

    case ActionType.CREATE_EVENT_FROM_FIREFLY:
      return checkValidity(state.set('isLoading', true));

    case ActionType.CREATE_EVENT_FROM_FIREFLY_SUCCESS:
      return defaultState;

    case ActionType.FETCHED_EVENT_INFO_DETAILS_SUCCESS: {
      const contactId = action.response.data.contacts?.find(contact => contact.isPrimary === true)?.id ?? 0;

      return checkValidity(
        state
          .setIn(['current', 'id'], fromJS(action.response.data.id))
          .setIn(['current', 'contactId', 'value'], contactId.toString())
          .setIn(['current', 'contacts'], fromJS(action.response.data.contacts))
          .setIn(['current', 'recordSubOwnerId', 'value'], action.response.data.analyst.id)
          .setIn(['current', 'recordSubOwnerName', 'value'], action.response.data.analyst.username)
          .setIn(['current', 'dealMakerId', 'value'], action.response.data.dealmaker.id)
          .setIn(['current', 'dealMakerName', 'value'], action.response.data.dealmaker.username)
          .setIn(['current', 'buyerId', 'value'], action.response.data.buyer.id)
          .setIn(['current', 'buyerName', 'value'], action.response.data.buyer.legalName)
          .setIn(['current', 'recordOwnerId', 'value'], action.response.data.module.id)
          .setIn(['current', 'recordOwnerName', 'value'], action.response.data.module.username)
          .setIn(['current', 'projectId', 'value'], action.response.data.project.id)
          .setIn(['current', 'projectName', 'value'], action.response.data.project.category),
      );
    }

    case ActionType.FETCHED_COMPLETED_EVENTS: {
      return state.set('suggestCompletedEvent', Map()).set('isLoadingCompletedEvents', true);
    }

    case ActionType.FETCHED_COMPLETED_EVENTS_SUCCESS: {
      return state.set('suggestCompletedEvent', fromJS(action.response.data)).set('isLoadingCompletedEvents', false);
    }

    case ActionType.FETCHED_COMPLETED_EVENTS_FAILURE: {
      return state.set('isLoadingCompletedEvents', false);
    }

    case ActionType.CLOSE_VALIDATION_ERROR:
      return state.deleteIn(['current', 'inputErrors', action.field]);

    case ActionType.RESET_MEETING_POPUP_FORM:
      return defaultState;

    default:
      return state;
  }
}

const checkValidity = state => {
  const plainState = toJS(state.get('current'));
  const validator = getValidator(plainState.companyType === 'target' ? SCHEMA_TARGET : SCHEMA_BUYER);
  const isValid = validator.isValid(plainState);
  const inputErrors = validator.getFirstError(plainState);

  return state.setIn(['current', 'isValid'], isValid).setIn(['current', 'inputErrors'], fromJS(inputErrors || {}));
};

const getParticipantsTextList = participants =>
  participants
    .map(param => {
      if (param.displayName === '' && param.email === '') {
        return param.phone;
      }

      if (param.displayName === '' || !param.displayName) {
        return param.email;
      }

      return param.displayName;
    })
    .filter(value => value)
    .join(', ');
